import { ProtectedComponent } from '@components'

import { Space, Spacing } from '@billy/design-system'
import { Button } from '@shinetools/lumen-react'
import { ChevronRight, FileSpreadsheet } from 'lucide-react'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { BankStatementDropzone } from '@features/accounts/elements/BankStatementDropzone'
import { useBankTransactionsAccess } from '@features/bankTransactions/hooks/useBankTransactionsAccess'
import { MastercardTrustNote } from '@features/openBanking/elements/MastercardTrustNote'
import { PopularBanksTilesList } from '@features/openBanking/elements/PopularBanksTilesList'
import { usePopularBanksSearch } from '@features/openBanking/hooks/usePopularBanksSearch'
import type { UiSourceValue } from '@features/reconciliation/enums'
import { ConnectShineBanner } from '@features/shineBanking/elements/ConnectShineBanner'
import { useCanConnectToShineBank } from '@features/shineBanking/hooks/useCanConnectToShineBank'

import { Scope } from '../../../../enums/scope'
import { useSegment } from '../../../../hooks'
import { BankSearch } from './elements/BankSearch'
import { BankView } from './enums/bankView'
import * as Styled from './styles'

const POPULAR_BANKS_LIST_LENGTH = 9

interface ConnectBankContentProps {
  bankView: BankView
  uiSource?: UiSourceValue
  onImportCsvClick?: () => void
}

export const ConnectBankContent = ({ bankView, uiSource, onImportCsvClick }: ConnectBankContentProps) => {
  const { t } = useTranslation()
  const { track } = useSegment()
  const { isTransactionsOnboardingEnabled } = useBankTransactionsAccess()
  const { canConnectToShineBank } = useCanConnectToShineBank()
  const { prefilledSearch, popularBanks, shouldShowPopularBanksList, handleSearchPrefill, handleClear } =
    usePopularBanksSearch(POPULAR_BANKS_LIST_LENGTH)

  const shouldShowDropzone = bankView === BankView.BankReconciliation
  const withModalOffset = bankView === BankView.ConnectModal

  const handleImportCsvClick = useCallback(() => {
    track('Reconciliation CSV Account Created (Client)')
    onImportCsvClick?.()
  }, [track, onImportCsvClick])

  return (
    <>
      {!isTransactionsOnboardingEnabled && canConnectToShineBank && (
        <ProtectedComponent scopes={Scope.IdentityMergeWrite}>
          <ConnectShineBanner />
          <Space size="s32" />
        </ProtectedComponent>
      )}
      <Styled.TrustNoteWrapper withModalOffset={withModalOffset}>
        <MastercardTrustNote />
      </Styled.TrustNoteWrapper>
      <BankSearch prefilledSearch={prefilledSearch} onClear={handleClear} uiSource={uiSource} />
      <Space size="s24" />
      {shouldShowPopularBanksList && (
        <>
          <PopularBanksTilesList
            banks={popularBanks}
            onSearchPrefill={handleSearchPrefill}
            tileSize="s"
            uiSource={uiSource}
          />
          <Space size="s24" />
        </>
      )}
      {shouldShowDropzone && <BankStatementDropzone />}
      {onImportCsvClick && (
        <ProtectedComponent scopes={Scope.BankStatementImport}>
          <Button
            variant="text"
            startIcon={<FileSpreadsheet />}
            endIcon={<ChevronRight />}
            label={t('bankreconciliation.connect_bank.import_csv')}
            style={{ fontWeight: 'var(--lumen-font-weight-regular)', marginLeft: `-${Spacing.S12}` }}
            onClick={handleImportCsvClick}
          />
        </ProtectedComponent>
      )}
    </>
  )
}
